import { useState } from 'react';
import { Pencil } from 'lucide-react';
import { useTranslation } from '../../../lib/i18n';
import { updateRoutine, type WebRoutine } from '../../../lib/routines';
import { canSaveRoutineEdit, routineEdit, type RoutineEditDraft } from '../../../lib/routine-edit';
import { IconButton, Input } from '../ui';
import styles from './routines.module.css';

/**
 * Where a save failure belongs on the form. A rejected CRON is attached to the SCHEDULE field rather than
 * shown as a form-level line, because "couldn't save" does not tell you which of four fields to fix — and
 * a routine with a cron the server won't take simply stops firing. Mirrors iOS `RoutineEditError`.
 */
export type RoutineEditError = { field: 'schedule' | 'form'; message: string };

/**
 * The routine's editable fields — name, description and cron — written through the
 * `update-routine` action both phones use (see `lib/routines.ts`).
 *
 * **Only what changed is sent**, and an untouched Save writes nothing at all: the rule is the pure
 * `routineEdit` diff in `lib/routine-edit.ts`, shared with both natives and unit-tested there.
 *
 * The cron is never typed here. The Schedule row reads the schedule in WORDS and its pencil opens the
 * Edit Schedule step, which stages a new cron back into this draft (`RoutineScheduleStep`). A cron the
 * picker cannot express is not coerced — that step routes to the chat instead.
 */
export function useRoutineEdit(routine: WebRoutine, onSaved: () => void) {
  const { t } = useTranslation();
  const [draft, setDraft] = useState<RoutineEditDraft>({
    name: routine.name ?? '',
    description: routine.description ?? '',
    cron: routine.cron ?? '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<RoutineEditError | null>(null);

  const patch = (part: Partial<RoutineEditDraft>) => setDraft((current) => ({ ...current, ...part }));
  const canSave = canSaveRoutineEdit(draft);

  const save = async (): Promise<boolean> => {
    if (!canSave) return false;
    const edit = routineEdit(routine, draft);
    // Nothing changed — close without a request. Restating unchanged values would clobber a concurrent
    // edit from the agent's own tools, and the server answers `400 no_updates` to an empty write.
    if (!edit) {
      onSaved();
      return true;
    }
    setSaving(true);
    setError(null);
    const result = await updateRoutine(routine, edit);
    setSaving(false);
    if (!result.ok) {
      setError(
        result.reason === 'invalid_cron'
          ? { field: 'schedule', message: t('routines.edit.error.invalidCron') }
          : { field: 'form', message: t('routines.edit.error.generic') },
      );
      return false;
    }
    onSaved();
    return true;
  };

  return {
    fields: {
      name: draft.name,
      setName: (name: string) => patch({ name }),
      description: draft.description,
      setDescription: (description: string) => patch({ description }),
      cron: draft.cron,
      setCron: (cron: string) => patch({ cron }),
    },
    saving,
    canSave,
    error,
    save,
  };
}

export function RoutineEditForm({
  fields,
  error,
  scheduleText,
  onEditSchedule,
}: {
  fields: ReturnType<typeof useRoutineEdit>['fields'];
  error: RoutineEditError | null;
  /** The staged schedule in words — "Every Monday at 9:00", never the cron. */
  scheduleText: string;
  onEditSchedule: () => void;
}) {
  const { t } = useTranslation();
  const scheduleError = error?.field === 'schedule' ? error.message : null;
  return (
    <div className={styles.form}>
      <Input
        label={t('routines.edit.name')}
        value={fields.name}
        onChange={(event) => fields.setName(event.target.value)}
      />
      <Input
        label={t('routines.edit.description')}
        value={fields.description}
        onChange={(event) => fields.setDescription(event.target.value)}
      />
      {/* Read-only by construction (Figma 18352-340851): the box is the DS field, but it takes no caret
          and raises no keyboard — the pencil beside it is the control. A cron typed by hand was the old
          affordance and it asked every reader to know cron. The wrapper carries the click so tapping the
          field works the way it does on both phones, and the PENCIL is the real focusable labelled
          control, so the wrapper stays a plain div.
          It ignores a click that landed ON that pencil: the pencil already calls `onEditSchedule`, and
          the bubbled click called it a SECOND time — opening the handoff and firing
          `change_schedule_in_chat` twice. One guard here rather than a `stopPropagation` per control,
          because the DS `IconButton`'s `onClick` takes no event to stop; same rule the WhatsApp channel
          cards use (`orion-web-ds-components.md`). */}
      <div
        className={styles.scheduleRead}
        onClick={(event) => {
          if ((event.target as HTMLElement).closest('a,button')) return;
          onEditSchedule();
        }}
      >
        <Input
          label={t('routines.edit.cron')}
          status={scheduleError ? 'error' : 'default'}
          statusMessage={scheduleError ?? undefined}
          value={scheduleText}
          // The lock is the NATIVE one: `aria-readonly` is only an announcement and `tabIndex={-1}` only
          // skips sequential tabbing, so without this a screen-reader's form navigation or a
          // programmatic focus could still put a caret here and have the no-op onChange eat the typing.
          // What `readOnly` must NOT bring is the DS read-only FILL — the grey of a value you cannot act
          // on, where this row is the press target and Figma draws it like the two fields above it. So
          // `.scheduleRead` overrides the fill alone; the ladder and the lock are untouched.
          readOnly
          onChange={() => {}}
          tabIndex={-1}
          endAdornment={
            <IconButton
              size="small"
              variant="text"
              onClick={onEditSchedule}
              aria-label={t('routines.schedule.edit.title')}
              title={t('routines.schedule.edit.title')}
            >
              <Pencil size={16} aria-hidden />
            </IconButton>
          }
        />
      </div>
      {error?.field === 'form' && <span className={styles.error}>{error.message}</span>}
    </div>
  );
}
